import { supabase } from '../supabase/client';
import { createLink, removeLinkBetween } from '../entityLinks';

// Termin ↔ Ticket. Der Termin bleibt ein eigenständiges Kalenderobjekt (mit Outlook-Sync);
// die Verbindung liegt in entity_links (ticket → termin, link_type 'zuordnung') — dieselbe
// Bauweise wie ticket → objekt/mieter/einheit. Dadurch kann ein Ticket beliebig viele
// Termine haben und ein Termin bleibt löschbar, ohne das Ticket anzufassen.
//
// Abgrenzung (User-Vorgabe): Ein Termin ist ein echtes Ereignis („Installateur kommt Freitag
// 14 Uhr"). Die Wiedervorlage bleibt davon getrennt — sie ist die interne Erinnerung.

export interface TicketTermin {
  id: string;
  titel: string;
  typ: string | null;
  start_datum: string;
  end_datum: string | null;
  ganztaegig: boolean | null;
  status: string | null;
  objekt_id: string | null;
}

const FELDER = 'id, titel, typ, start_datum, end_datum, ganztaegig, status, objekt_id';

// ── Reine Regeln ─────────────────────────────────────────────────────────────

/** Abgesagte Termine zählen nicht mehr als Vorhaben. */
export function istAktiv(t: Pick<TicketTermin, 'status'>): boolean {
  return (t.status || '').toLowerCase() !== 'abgesagt';
}

export function sortiereTermine<T extends Pick<TicketTermin, 'start_datum'>>(termine: T[]): T[] {
  return [...termine].sort((a, b) => (a.start_datum || '').localeCompare(b.start_datum || ''));
}

/**
 * „Was passiert als Nächstes?" — der früheste noch bevorstehende, nicht abgesagte Termin.
 * Liegt alles in der Vergangenheit, gibt es keinen nächsten (null) — dann zeigt das Ticket
 * die Liste, aber keine Vorschau.
 */
export function naechsterTermin<T extends Pick<TicketTermin, 'start_datum' | 'status'>>(
  termine: T[], jetztIso: string,
): T | null {
  const kommend = sortiereTermine(termine).filter((t) => istAktiv(t) && (t.start_datum || '') >= jetztIso);
  return kommend[0] ?? null;
}

/** „Di., 11.08. · 10:00–11:00 Uhr" bzw. „Di., 11.08. · ganztägig". */
export function terminZeitText(t: Pick<TicketTermin, 'start_datum' | 'end_datum' | 'ganztaegig'>): string {
  const start = new Date(t.start_datum);
  if (Number.isNaN(start.getTime())) return '—';
  const tag = start.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
  if (t.ganztaegig) return `${tag} · ganztägig`;
  const uhr = (d: Date) => d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  const ende = t.end_datum ? new Date(t.end_datum) : null;
  const bis = ende && !Number.isNaN(ende.getTime()) ? `–${uhr(ende)}` : '';
  return `${tag} · ${uhr(start)}${bis} Uhr`;
}

// ── Datenzugriff ─────────────────────────────────────────────────────────────

export async function ladeTicketTermine(ticketId: string): Promise<TicketTermin[]> {
  const { data: links } = await supabase
    .from('entity_links').select('target_id')
    .eq('source_type', 'ticket').eq('source_id', ticketId)
    .eq('target_type', 'termin').eq('link_type', 'zuordnung');
  const ids = (links || []).map((l: { target_id: string }) => l.target_id);
  if (!ids.length) return [];
  const { data } = await supabase.from('termine').select(FELDER).in('id', ids);
  return sortiereTermine((data || []) as TicketTermin[]);
}

/** Das Ticket zu einem Termin (für den Rücksprung aus dem Kalender). */
export async function ladeTicketZuTermin(
  terminId: string,
): Promise<{ id: string; ticket_nummer: string | null; titel: string | null } | null> {
  const { data: links } = await supabase
    .from('entity_links').select('source_id')
    .eq('target_type', 'termin').eq('target_id', terminId)
    .eq('source_type', 'ticket').eq('link_type', 'zuordnung').limit(1);
  const ticketId = links?.[0]?.source_id;
  if (!ticketId) return null;
  const { data } = await supabase.from('tickets').select('id, ticket_nummer, titel').eq('id', ticketId).maybeSingle();
  return (data as { id: string; ticket_nummer: string | null; titel: string | null }) || null;
}

/** Ticket-Bezug für eine ganze Terminliste (Kalender) — eine Abfrage statt einer je Termin. */
export async function ladeTicketsZuTerminen(
  terminIds: string[],
): Promise<Record<string, { id: string; ticket_nummer: string | null; titel: string | null }>> {
  if (!terminIds.length) return {};
  const { data: links } = await supabase
    .from('entity_links').select('source_id, target_id')
    .eq('source_type', 'ticket').eq('target_type', 'termin').eq('link_type', 'zuordnung')
    .in('target_id', terminIds);
  const paare = (links || []) as { source_id: string; target_id: string }[];
  if (!paare.length) return {};
  const { data: tickets } = await supabase
    .from('tickets').select('id, ticket_nummer, titel').in('id', [...new Set(paare.map((l) => l.source_id))]);
  const ticketById = new Map((tickets || []).map((t: any) => [t.id, t]));
  const ergebnis: Record<string, { id: string; ticket_nummer: string | null; titel: string | null }> = {};
  for (const l of paare) {
    const t = ticketById.get(l.source_id);
    if (t) ergebnis[l.target_id] = t;
  }
  return ergebnis;
}

export async function verknuepfeTicketTermin(ticketId: string, terminId: string): Promise<void> {
  const { error } = await createLink({
    sourceType: 'ticket', sourceId: ticketId,
    targetType: 'termin', targetId: terminId, linkType: 'zuordnung',
  });
  if (error) throw new Error(error.message);
}

export async function loeseTicketTermin(ticketId: string, terminId: string): Promise<void> {
  const { error } = await removeLinkBetween('ticket', ticketId, 'termin', terminId, 'zuordnung');
  if (error) throw new Error(error.message);
}

/**
 * Termin-Ereignisse im Ticket-Verlauf festhalten — sonst sieht man später nicht, warum ein
 * Ticket wartet. Best effort: ein fehlgeschlagener Eintrag darf das Speichern nie kippen.
 */
export async function protokolliereTermin(
  ticketId: string, aktion: string, text: string, benutzerId?: string | null,
): Promise<void> {
  try {
    await supabase.from('ticket_historie').insert({
      ticket_id: ticketId, benutzer_id: benutzerId ?? null, aktion, neuer_wert: text,
    });
  } catch (e) {
    console.warn('Termin-Eintrag im Ticket-Verlauf fehlgeschlagen', (e as Error).message);
  }
}
